import { OPERATIONAL_EVENT_TYPE } from '@atlaskit/analytics-gas-types';
import { useFirePostOfficeAnalyticsEvents } from '@post-office/analytics';
import { type ErrorInfo } from 'react';
import {
	ErrorBoundary,
	type ErrorBoundaryPropsWithRender,
	type FallbackProps,
} from 'react-error-boundary';

export type ErrorBoundaryProps = Partial<
	Pick<ErrorBoundaryPropsWithRender, 'fallbackRender' | 'onError'>
>;
export type ErrorBoundaryAdditionalAnalytics = {
	errorBoundaryLocation?: string;
	placementId?: string;
	messageTemplateId?: string;
};

export type PlacementErrorBoundaryProps = Partial<ErrorBoundaryPropsWithRender> &
	ErrorBoundaryAdditionalAnalytics;

type FallbackRender = (props: FallbackProps) => React.ReactNode;

export const withErrorBoundary =
	<P extends PlacementErrorBoundaryProps & Record<string, unknown>>(
		Component: React.FC<P>,
	): React.FC<P> =>
	(props) => {
		const { errorBoundaryLocation, placementId, messageTemplateId, fallbackRender, onError } =
			props;

		const { fireAnalyticsEvent } = useFirePostOfficeAnalyticsEvents();

		const internalOnError =
			(
				additionalAnalytics: ErrorBoundaryAdditionalAnalytics,
				onError?: ErrorBoundaryProps['onError'],
			) =>
			(error: Error, info: ErrorInfo) => {
				if (onError) {
					onError(error, info);
				}
				fireAnalyticsEvent(
					{
						eventType: OPERATIONAL_EVENT_TYPE,
						action: 'caughtError',
						actionSubject: 'PostOfficeErrorBoundary',
						actionSubjectId: additionalAnalytics.errorBoundaryLocation,
					},
					{
						error: error,
						info: info,
						errorBoundaryLocation: additionalAnalytics.errorBoundaryLocation,
						placementId: additionalAnalytics.placementId,
						messageTemplateId: additionalAnalytics.messageTemplateId,
					},
				);
			};

		const internalFallbackRender =
			(FallbackRender?: FallbackRender): FallbackRender =>
			(props) => {
				if (FallbackRender) {
					// eslint-disable-next-line @typescript-eslint/ban-ts-comment
					// @ts-ignore - Confluence Local Consumption - 'FallbackRender' cannot be used as a JSX component. Its return type 'ReactNode' is not a valid JSX element. Type 'undefined' is not assignable to type 'Element | null'.
					return <FallbackRender {...props} />;
				}
				return null;
			};

		return (
			<ErrorBoundary
				fallbackRender={internalFallbackRender(fallbackRender)}
				onError={internalOnError(
					{ errorBoundaryLocation, placementId, messageTemplateId },
					onError,
				)}
			>
				<Component {...props} />
			</ErrorBoundary>
		);
	};
